// Pure helpers for the Shop panel: money in/out, stock display, status labels,
// option/SKU mirrors of the server rules, discount values. No React/store
// imports so it stays unit-testable. Money formatting is the Payments panel's.
import type { ShippingAddress } from '@dunea/shared';
import type { DiscountType, OptionSetInput, Order, OrderStatus, ProductStatus, Variant } from '~/lib/api/shop';
import {
  currencyExponent,
  formatMinor,
  majorToMinor,
  type Tone,
} from '~/components/workbench/tools/payments/payments-model';

export { apiErrorMessage, formatMinor, minorToMajorInput } from '~/components/workbench/tools/payments/payments-model';

// ─── money ───

/**
 * Owner-typed major amount → minor units. Like Payments' majorToMinor, but a
 * price of zero is allowed when `allowZero` (free items, free shipping).
 */
export function parseMoney(input: string, currency: string, opts: { allowZero?: boolean } = {}): number | null {
  const clean = input.trim().replace(/,/g, '');

  if (opts.allowZero && /^0+(?:\.0*)?$/.test(clean)) {
    const frac = clean.split('.')[1] ?? '';
    return frac.length <= currencyExponent(currency) ? 0 : null;
  }

  return majorToMinor(input, currency);
}

/** Optional money field: blank → null (cleared), otherwise parsed; `undefined` = invalid. */
export function parseOptionalMoney(input: string, currency: string): number | null | undefined {
  if (!input.trim()) {
    return null;
  }

  return parseMoney(input, currency, { allowZero: true }) ?? undefined;
}

/** "₹499" or "₹499 – ₹799" across the live (non-archived) variants. */
export function priceRangeLabel(variants: Pick<Variant, 'price' | 'archivedAt'>[], currency: string): string {
  const prices = variants.filter((v) => !v.archivedAt).map((v) => v.price);

  if (!prices.length) {
    return '—';
  }

  const min = Math.min(...prices);
  const max = Math.max(...prices);

  return min === max ? formatMinor(min, currency) : `${formatMinor(min, currency)} – ${formatMinor(max, currency)}`;
}

// ─── percent ↔ basis points ───

/** "15" → 1500, "12.5" → 1250. Range 0.01–100%. */
export function percentToBps(input: string): number | null {
  const match = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(input.trim());

  if (!match) {
    return null;
  }

  const bps = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0') || '0');

  return bps >= 1 && bps <= 10_000 ? bps : null;
}

/**
 * A product's tax-rate field: "" → null (no tax), "0" → 0, "18" → 1800,
 * "12.5" → 1250. undefined = not a valid rate (0–100%, up to 2 decimals).
 */
export function parseTaxRate(input: string): number | null | undefined {
  const text = input.trim().replace(/%$/, '').trim();

  if (!text) {
    return null;
  }

  return /^0+(?:\.0{1,2})?$/.test(text) ? 0 : (percentToBps(text) ?? undefined);
}

/** 1800 → "18", 1250 → "12.5", null → "". */
export function taxRateInput(bps: number | null): string {
  return bps === null ? '' : String(Number((bps / 100).toFixed(2)));
}

export function bpsToPercentLabel(bps: number): string {
  return `${Number((bps / 100).toFixed(2))}%`;
}

export function discountValueLabel(d: { type: DiscountType; value: number }, currency: string): string {
  switch (d.type) {
    case 'percent':
      return `${bpsToPercentLabel(d.value)} off`;
    case 'fixed':
      return `${formatMinor(d.value, currency)} off`;
    default:
      return 'Free shipping';
  }
}

export const DISCOUNT_CODE_RE = /^[A-Z0-9_-]{2,40}$/;

// ─── whole numbers (stock, thresholds, limits) ───

/** A whole number ≥ 0 ("12"), else null. */
export function parseCount(input: string): number | null {
  const clean = input.trim();
  return /^\d{1,9}$/.test(clean) ? Number(clean) : null;
}

/** A non-zero signed whole number ("+5", "-3", "4"), else null. */
export function parseDelta(input: string): number | null {
  const match = /^([+-]?)(\d{1,9})$/.exec(input.trim().replace(/\s+/g, ''));

  if (!match || Number(match[2]) === 0) {
    return null;
  }

  return match[1] === '-' ? -Number(match[2]) : Number(match[2]);
}

// ─── stock display (mirrors studio shop/domain/stock.ts) ───

export function availableQty(v: Pick<Variant, 'stock' | 'reserved'>): number | null {
  return v.stock === null ? null : Math.max(v.stock - v.reserved, 0);
}

/** One variant: "Untracked", "12 in stock", "12 in stock · 2 held", "Out of stock". */
export function variantStockLabel(v: Pick<Variant, 'stock' | 'reserved' | 'allowBackorder'>): {
  label: string;
  tone: Tone;
} {
  if (v.stock === null) {
    return { label: 'Untracked', tone: 'muted' };
  }

  const held = v.reserved > 0 ? ` · ${v.reserved} held` : '';

  if (v.stock <= 0) {
    return { label: `Out of stock${held}${v.allowBackorder ? ' · backorders on' : ''}`, tone: 'danger' };
  }

  return { label: `${v.stock} in stock${held}`, tone: 'muted' };
}

/**
 * A product's total on-hand stock across live variants: "Untracked" when none
 * are tracked; "24 in stock" (+ " · some untracked" when mixed).
 */
export function productStockLabel(variants: Pick<Variant, 'stock' | 'archivedAt'>[]): { label: string; tone: Tone } {
  const live = variants.filter((v) => !v.archivedAt);
  const tracked = live.filter((v) => v.stock !== null);

  if (!tracked.length) {
    return { label: 'Untracked', tone: 'muted' };
  }

  const total = tracked.reduce((sum, v) => sum + (v.stock ?? 0), 0);
  const mixed = tracked.length < live.length ? ' · some untracked' : '';

  return { label: `${total} in stock${mixed}`, tone: total === 0 ? 'danger' : 'muted' };
}

// ─── statuses ───

const PRODUCT_STATUS: Record<ProductStatus, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'muted' },
  active: { label: 'Active', tone: 'success' },
  archived: { label: 'Archived', tone: 'muted' },
};

export function productStatusMeta(status: string): { label: string; tone: Tone } {
  return PRODUCT_STATUS[status as ProductStatus] ?? { label: status, tone: 'muted' };
}

export const PRODUCT_STATUSES: readonly ProductStatus[] = ['draft', 'active', 'archived'];

const ORDER_STATUS: Record<OrderStatus, { label: string; tone: Tone; attention?: boolean }> = {
  pending: { label: 'Awaiting payment', tone: 'warning' },
  paid: { label: 'Paid', tone: 'success' },
  fulfilled: { label: 'Fulfilled', tone: 'info' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
  expired: { label: 'Expired', tone: 'muted' },
  refunded: { label: 'Refunded', tone: 'muted' },
  partially_refunded: { label: 'Partly refunded', tone: 'info' },
  disputed: { label: 'Disputed', tone: 'danger', attention: true },
  needs_refund: { label: 'Needs refund', tone: 'danger', attention: true },
};

/** `attention` marks orders the owner must act on (needs_refund, disputed). */
export function orderStatusMeta(status: string): { label: string; tone: Tone; attention: boolean } {
  const meta = ORDER_STATUS[status as OrderStatus];
  return meta ? { ...meta, attention: Boolean(meta.attention) } : { label: status, tone: 'muted', attention: false };
}

export const ORDER_STATUS_FILTERS: readonly (readonly [string, string, OrderStatus[] | undefined])[] = [
  ['all', 'All', undefined],
  ['open', 'To fulfil', ['paid']],
  ['attention', 'Needs attention', ['needs_refund', 'disputed']],
  ['pending', 'Awaiting payment', ['pending']],
  ['done', 'Fulfilled', ['fulfilled']],
  ['refunded', 'Refunded', ['refunded', 'partially_refunded']],
  ['closed', 'Cancelled', ['cancelled', 'expired']],
];

const REFUNDABLE: readonly OrderStatus[] = ['paid', 'fulfilled', 'partially_refunded', 'needs_refund', 'disputed'];

/** What the owner can do with an order (mirrors ShopService.fulfil / cancel / refundOrder). */
export function orderActions(order: Pick<Order, 'status' | 'paymentId'>): {
  fulfil: boolean;
  cancel: boolean;
  refund: boolean;
} {
  return {
    fulfil: order.status === 'paid' || order.status === 'fulfilled',
    cancel: order.status === 'pending',
    refund: REFUNDABLE.includes(order.status) && Boolean(order.paymentId),
  };
}

/** Tax added on top (exclusive) vs already inside the prices — read off the order's own amounts. */
export function orderTaxExclusive(order: Pick<Order, 'subtotal' | 'discount' | 'shipping' | 'tax' | 'total'>): boolean {
  return order.tax > 0 && order.total === order.subtotal - order.discount + order.shipping + order.tax;
}

export function addressLines(address: ShippingAddress | null | undefined): string[] {
  if (!address || typeof address !== 'object') {
    return [];
  }

  const cityLine = [address.city, address.state, address.postalCode].filter(Boolean).join(', ');

  return [address.name, address.line1, address.line2, cityLine, address.country, address.phone].filter(
    (line): line is string => typeof line === 'string' && line.trim().length > 0,
  );
}

export function buyerLabel(order: Pick<Order, 'buyerName' | 'buyerEmail'>): string {
  return order.buyerName || order.buyerEmail || 'Guest';
}

// ─── options (mirrors studio shop/domain/variants.ts normalizeOptionSets) ───

export const MAX_OPTION_SETS = 3;
export const MAX_VARIANTS = 100;

export interface OptionDraft {
  name: string;
  /** Comma-separated values as typed. */
  values: string;
}

/** Comma-separated values → trimmed, non-empty list. */
export function splitValues(text: string): string[] {
  return text
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

/** Option drafts → validated option sets, or the first problem. Blank rows are ignored. */
export function parseOptionDrafts(
  drafts: OptionDraft[],
): { ok: true; options: OptionSetInput[]; variantCount: number } | { ok: false; error: string } {
  const rows = drafts.filter((d) => d.name.trim() || d.values.trim());

  if (rows.length > MAX_OPTION_SETS) {
    return { ok: false, error: `A product can have at most ${MAX_OPTION_SETS} options.` };
  }

  const names = new Set<string>();
  const options: OptionSetInput[] = [];

  for (const row of rows) {
    const name = row.name.trim();

    if (!name) {
      return { ok: false, error: 'Every option needs a name, e.g. "Size".' };
    }

    if (names.has(name.toLowerCase())) {
      return { ok: false, error: `Option "${name}" is listed twice.` };
    }

    names.add(name.toLowerCase());

    const values = splitValues(row.values);

    if (!values.length) {
      return { ok: false, error: `Option "${name}" needs at least one value.` };
    }

    const seen = new Set<string>();

    for (const value of values) {
      if (seen.has(value.toLowerCase())) {
        return { ok: false, error: `Option "${name}" lists "${value}" twice.` };
      }

      seen.add(value.toLowerCase());
    }

    options.push({ name, values });
  }

  const variantCount = options.reduce((n, o) => n * o.values.length, 1);

  if (variantCount > MAX_VARIANTS) {
    return { ok: false, error: `These options make ${variantCount} variants; the limit is ${MAX_VARIANTS}.` };
  }

  return { ok: true, options, variantCount };
}

export function optionDraftsFrom(sets: { name: string; values: string[] }[]): OptionDraft[] {
  return sets.map((s) => ({ name: s.name, values: s.values.join(', ') }));
}

/** "Blue / M", or "Default" for a product without options. */
export function variantTitle(options: string[]): string {
  return options.length ? options.join(' / ') : 'Default';
}

/** Comma-separated tags → trimmed, de-duplicated (case-insensitive, first spelling wins). */
export function parseTags(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const tag of splitValues(text)) {
    if (!seen.has(tag.toLowerCase())) {
      seen.add(tag.toLowerCase());
      out.push(tag);
    }
  }

  return out;
}

// ─── SKU preview (mirrors studio shop/domain/sku.ts) ───

export const DEFAULT_SKU_PATTERN = '{PRODUCT}-{OPTION1}-{OPTION2}';

export function normalizeSku(raw: string): string {
  const folded = raw.normalize('NFKD').replace(/\p{M}/gu, '');
  const cleaned = folded
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return cleaned.slice(0, 64).replace(/-+$/, '');
}

export function previewSku(
  pattern: string | null | undefined,
  input: { product: string; options: readonly string[]; n?: number },
): string {
  const template = pattern?.trim() || DEFAULT_SKU_PATTERN;
  const raw = template.replace(/\{(PRODUCT|OPTION([1-3])|N)\}/gi, (_m, token: string, index?: string) => {
    const upper = token.toUpperCase();

    if (upper === 'PRODUCT') {
      return input.product;
    }

    if (upper === 'N') {
      return String(input.n ?? 1);
    }

    return input.options[Number(index) - 1] ?? '';
  });

  return normalizeSku(raw) || 'SKU';
}

// ─── modules ───

export const SHOP_MODULE_DESCRIPTIONS: Record<string, string> = {
  catalog: 'Products, collections and the storefront listing.',
  variants: 'Options like size and colour, each with its own SKU.',
  inventory: 'Stock counts, reservations and low-stock alerts.',
  pricing: 'Prices and compare-at prices.',
  orders: 'Orders, fulfilment and refunds.',
  customers: 'Buyer details across orders.',
  discounts: 'Discount codes at checkout.',
  shipping: 'Shipping charges on the cart.',
  tax: 'Tax on orders, included or added on top.',
  notifications: 'Order emails to you and your buyers.',
  reviews: 'Product reviews.',
  digital: 'Downloadable products.',
  'abandoned-carts': 'Follow up on checkouts that were not finished.',
  analytics: 'Sales reports.',
};

export function moduleLabel(id: string): string {
  return id
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ─── dates ───

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export function formatDate(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }

  const d = new Date(iso);

  return Number.isNaN(d.getTime()) ? '' : dateFormat.format(d);
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export function isHttpUrl(url: string): boolean {
  return /^https?:\/\/\S+$/i.test(url.trim());
}
